import { useEffect, useState } from 'react'

/**
 * useRoadmap — the client's setup picture, from the SAME server derivation that
 * renders their phone page.
 *
 * ── WHY A SERVER CALL AND NOT A FIRESTORE READ (John, 2026-09-14) ────────────
 * *"It won't seem like they're dealing with the same system anymore if they check
 * it on their phone and then log in to the Control Center and see things there."*
 *
 * The Setup page used to derive its own picture in the BROWSER from
 * `postOnboardingOrchestrator/state` — a different store and a different model
 * (per-agent STEPS, not per-skill GATES). Measured on a live client the same day:
 * his completed steps included a SOCIAL step for a skill he does not own, while
 * his roadmap said five skills, none running. Deriving client-facing state in the
 * browser is how two surfaces stop agreeing, and it is the same class as the
 * Dashboard counting leads locally instead of using the shared attribution.
 */

import { isDemoMode, type RoadmapView } from '@bas/shared'
import { DEMO_ROADMAP_VIEW } from './demoRoadmapView'
export type {
  GateStatusMark, RoadmapState, RoadmapGate, RoadmapSection, RoadmapView,
  RoadmapBox, RoadmapItem,
} from '@bas/shared'
export { countNeedsClient } from '@bas/shared'

export interface RoadmapResult {
  view: RoadmapView | null
  loading: boolean
  /**
   * ⚠️ A DISTINCT ERROR STATE, NOT AN EMPTY VIEW. A failed read must render as
   * "we couldn't load this", never as a client with nothing set up — the page
   * would otherwise invent a status it never received.
   */
  error: string | null
  /** Re-read the same derivation now (after the page itself changed something). */
  refresh: () => void
}

export function useRoadmap(clientId: string | null | undefined): RoadmapResult {
  const [view, setView] = useState<RoadmapView | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Bumped by the live listeners below; each bump re-reads the SAME derivation.
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    if (!clientId) { setLoading(false); return }

    ;(async () => {
      // ⚠️ A LIVE REFRESH NEVER BLANKS THE PAGE. Only the first read shows the
      // loading state; later reads swap the view in place when they land, so a
      // finished connect simply appears.
      if (tick === 0) { setLoading(true); setError(null) }
      try {
        const { auth } = await import('@bas/shared')
        const user = auth.currentUser
        // ⚠️ NOT SIGNED IN IS NOT A LOAD FAILURE. Demo mode deliberately never
        // authenticates, so throwing here would paint the public showcase with a
        // red "we couldn't load your setup" card. No user → the demo fixture in
        // demo mode, otherwise no view and no error.
        if (!user) {
          if (!cancelled) { setView(isDemoMode() ? DEMO_ROADMAP_VIEW : null); setLoading(false) }
          return
        }
        const idToken = await user.getIdToken()
        const res = await fetch(
          `/api/setup/roadmap?clientId=${encodeURIComponent(clientId)}`,
          { headers: { Authorization: `Bearer ${idToken}` } },
        )
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const body = await res.json()
        if (!body?.ok || !body?.view) throw new Error(body?.reason || 'no-view')
        if (!cancelled) { setView(body.view as RoadmapView); setError(null) }
      } catch (err) {
        // A failed LIVE refresh keeps the view we have — it is still true as of
        // its last read — and only a failed FIRST read becomes the error card.
        if (!cancelled && tick === 0) setError((err as Error).message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => { cancelled = true }
  }, [clientId, tick])

  /* ── LIVE (Susan's setup, 2026-09-28 #3) ────────────────────────────────────
     A finished connect did not show until she REFRESHED — the page read the
     roadmap once. Every connect writes the client doc (tokens, the chosen Page,
     the listing) or a `connectedAccounts` doc (Outlook), so a change to either
     re-reads the server derivation. Returning to the tab does too: the connect
     happens in another tab, and that is the moment she looks back.

     ⚠️ IT RE-READS THE SERVER, it never patches the view from the snapshot —
     the snapshot is raw fields, and turning them into rows here would be the
     roadmap derived a second time, in the browser. */
  useEffect(() => {
    if (!clientId || isDemoMode()) return
    let timer: number | null = null
    const bump = () => {
      if (timer) window.clearTimeout(timer)
      // Debounced: one OAuth callback writes several fields in quick succession.
      timer = window.setTimeout(() => setTick((t) => t + 1), 1200)
    }
    const unsubs: Array<() => void> = []
    let stopped = false
    ;(async () => {
      try {
        const { db, auth } = await import('@bas/shared')
        if (!auth.currentUser || stopped) return
        const { doc, collection, onSnapshot } = await import('firebase/firestore')
        let first = true
        unsubs.push(onSnapshot(doc(db, 'clients', clientId), () => {
          if (first) { first = false; return } // the initial snapshot is the state we already read
          bump()
        }, () => { /* a listener we cannot open leaves the manual refresh — never an error card */ }))
        let firstAccts = true
        unsubs.push(onSnapshot(collection(db, 'clients', clientId, 'connectedAccounts'), () => {
          if (firstAccts) { firstAccts = false; return }
          bump()
        }, () => { /* see above */ }))
      } catch { /* no live updates; the page still works */ }
    })()
    const onFocus = () => { if (document.visibilityState === 'visible') bump() }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      stopped = true
      if (timer) window.clearTimeout(timer)
      unsubs.forEach((u) => u())
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [clientId])

  return { view, loading, error, refresh: () => setTick((t) => t + 1) }
}

